// ────────────────────────────────────────────────────────────────────────────
//  PatchNotes.tsx — dedizierte Release-Notes-Seite
//
//  Komplett client-seitig (kein API-Call). Daten kommen aus
//  `src/data/patchNotes.ts`. Features:
//    - Hero-Card pro Release (Version + Headline + Summary + Channel-Badge)
//    - Sticky Filter-Bar mit Suche + App-Filter + Kind-Filter
//    - Counts pro Filter
//    - Per-Release expand/collapse + per-Eintrag expand für Detail
//    - Releases-Timeline-Sidebar (Desktop) für Schnell-Sprung
//
//  Erreichbar via /patchnotes (Public — kein Auth nötig).
// ────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  RELEASES, APP_META, KIND_META, KIND_ORDER,
  type PatchApp, type PatchKind, type Release, type PatchEntry,
} from '../data/patchNotes'

const APPS: PatchApp[] = ['ios', 'webapp', 'backend']

export function PatchNotes() {
  const [search,      setSearch]      = useState('')
  const [appFilter,   setAppFilter]   = useState<PatchApp | 'all'>('all')
  const [kindFilter,  setKindFilter]  = useState<PatchKind | 'all'>('all')

  // Page-Title setzen wenn die Route aktiv ist
  useEffect(() => {
    const prev = document.title
    document.title = 'Patchnotes · CustoSoft'
    return () => { document.title = prev }
  }, [])

  // Gefilterte Releases — leere Releases werden ausgeblendet
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return RELEASES.map(rel => ({
      ...rel,
      entries: rel.entries.filter(e => {
        if (appFilter  !== 'all' && e.app  !== appFilter)  return false
        if (kindFilter !== 'all' && e.kind !== kindFilter) return false
        if (q) {
          const hay = (e.title + ' ' + (e.detail ?? '')).toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      }),
    })).filter(r => r.entries.length > 0)
  }, [search, appFilter, kindFilter])

  // Globale Counts (über alle Releases) für Filter-Chips
  const totalCounts = useMemo(() => {
    const all  = RELEASES.flatMap(r => r.entries)
    const byApp:  Record<PatchApp, number>  = { ios: 0, webapp: 0, backend: 0 }
    const byKind: Record<PatchKind, number> = { new: 0, changed: 0, fixed: 0, done: 0 }
    for (const e of all) { byApp[e.app]++; byKind[e.kind]++ }
    return { all: all.length, byApp, byKind }
  }, [])

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--bg)' }}>
      <BackgroundDecor />

      {/* Top-Nav */}
      <header className="relative z-10 px-6 py-5 flex items-center gap-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <Link
          to="/"
          className="text-sm font-semibold text-app-text-dim hover:text-app-text inline-flex items-center gap-2"
        >
          <span>←</span><span>Zur Übersicht</span>
        </Link>
        <span className="ml-auto text-[10px] font-bold tracking-widest text-app-text-mute">PATCHNOTES</span>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* ── Hero ── */}
        <section className="mb-10 md:mb-14 text-center">
          <div className="inline-block text-[11px] font-bold tracking-widest text-accent mb-2">
            RELEASE-NOTES
          </div>
          <h1 className="text-3xl md:text-5xl font-bold heading-fade mb-4">
            Was sich seit dem letzten Build getan hat.
          </h1>
          <p className="text-app-text-dim max-w-2xl mx-auto">
            Jeder Release listet was neu ist, was sich geändert hat und was wir behoben haben — auf
            iOS / iPadOS / macOS, in der Webapp und im Backend. Such, filter, vergleich.
          </p>
        </section>

        {/* ── Filter-Bar (sticky) ── */}
        <div className="sticky top-2 z-20 mb-8 backdrop-blur-md rounded-2xl border p-3 md:p-4 space-y-3"
             style={{ background: 'color-mix(in oklab, var(--bg-elev) 86%, transparent)',
                      borderColor: 'var(--border)' }}>
          {/* Such-Input + Reset */}
          <div className="flex items-center gap-2">
            <span className="text-app-text-mute text-sm shrink-0">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Suche in Titel oder Detail…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-app-text-mute"
            />
            {(search || appFilter !== 'all' || kindFilter !== 'all') && (
              <button
                onClick={() => { setSearch(''); setAppFilter('all'); setKindFilter('all') }}
                className="text-[11px] font-semibold text-app-text-dim hover:text-app-text px-2 py-1 rounded-full border"
                style={{ borderColor: 'var(--border)' }}
              >
                Filter zurücksetzen
              </button>
            )}
          </div>

          {/* Filter-Chips */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] font-bold tracking-widest text-app-text-mute mr-1">APP</span>
            <FilterChip
              label="Alle" count={totalCounts.all}
              active={appFilter === 'all'} onClick={() => setAppFilter('all')}
            />
            {APPS.map(a => (
              <FilterChip
                key={a}
                label={`${APP_META[a].emoji} ${APP_META[a].short}`}
                count={totalCounts.byApp[a]}
                accent={APP_META[a].accent}
                active={appFilter === a} onClick={() => setAppFilter(a)}
              />
            ))}
            <span className="text-[10px] font-bold tracking-widest text-app-text-mute mx-1 ml-3">TYP</span>
            <FilterChip
              label="Alle" count={totalCounts.all}
              active={kindFilter === 'all'} onClick={() => setKindFilter('all')}
            />
            {KIND_ORDER.map(k => (
              <FilterChip
                key={k}
                label={KIND_META[k].label}
                count={totalCounts.byKind[k]}
                accent={KIND_META[k].color}
                active={kindFilter === k} onClick={() => setKindFilter(k)}
              />
            ))}
          </div>
        </div>

        {/* ── Releases ── */}
        {filtered.length === 0 ? (
          <EmptyState onReset={() => { setSearch(''); setAppFilter('all'); setKindFilter('all') }} />
        ) : (
          <div className="space-y-10">
            {filtered.map((rel, i) => (
              <ReleaseSection key={rel.version} release={rel} expandedDefault={i === 0} />
            ))}
          </div>
        )}

        <footer className="mt-16 text-center text-xs text-app-text-mute">
          Älter als hier gelistet? Wir schreiben nur die Releases auf, die in der App sichtbar sind.
          <br />
          Fehlt was? <a href="mailto:taironic.media@gmail.com" className="underline hover:text-app-text">Schreib uns.</a>
        </footer>
      </main>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//  Komponenten
// ════════════════════════════════════════════════════════════════════════════

function FilterChip({
  label, count, accent, active, onClick,
}: {
  label: string; count: number; accent?: string; active: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold transition ${
        active ? 'border-accent/60 bg-accent/10' : 'border-app-border hover:bg-app-elev-2'
      }`}
      style={active && accent ? { borderColor: accent + '88', background: accent + '1a' } : undefined}
    >
      <span style={active && accent ? { color: accent } : undefined}>{label}</span>
      <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full"
            style={{ background: 'var(--bg-elev-2)', color: accent ?? 'var(--text-dim)' }}>
        {count}
      </span>
    </button>
  )
}

function ReleaseSection({ release, expandedDefault }: { release: Release; expandedDefault: boolean }) {
  const [expanded, setExpanded] = useState(expandedDefault)

  // Pro-App-Gruppen mit ihren Einträgen, in der Reihenfolge der KIND_ORDER
  const groups = useMemo(() => {
    return APPS.map(app => ({
      app,
      entries: release.entries.filter(e => e.app === app),
    })).filter(g => g.entries.length > 0)
  }, [release])

  if (groups.length === 0) return null

  const dateLocal = new Date(release.date).toLocaleDateString('de-DE', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <article id={`release-${release.version}`} className="glass-strong rounded-3xl overflow-hidden">
      {/* Hero des Releases */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full text-left p-5 md:p-7 border-b transition hover:bg-app-elev-2/30"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex flex-wrap items-start gap-4">
          <div className="shrink-0 flex flex-col items-center">
            <div className="text-2xl md:text-3xl font-bold tracking-tight">v{release.version}</div>
            {release.build && (
              <div className="text-[10px] font-bold tracking-widest text-app-text-mute mt-0.5">
                BUILD {release.build}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <ChannelBadge channel={release.channel} />
              <span className="text-xs text-app-text-mute">{dateLocal}</span>
            </div>
            <h2 className="text-lg md:text-xl font-bold leading-tight">{release.headline}</h2>
            <p className="mt-1.5 text-sm text-app-text-dim">{release.summary}</p>
          </div>
          <div className="shrink-0 flex items-center gap-2 self-center">
            {APPS.map(app => {
              const n = release.entries.filter(e => e.app === app).length
              if (n === 0) return null
              return (
                <span key={app} className="text-[10px] font-bold tracking-wider px-2 py-1 rounded-full"
                      style={{ background: APP_META[app].accent + '22', color: APP_META[app].accent }}>
                  {APP_META[app].emoji} {n}
                </span>
              )
            })}
            <span className="text-app-text-mute text-lg ml-2 transition-transform"
                  style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }}>›</span>
          </div>
        </div>
      </button>

      {/* Einträge */}
      {expanded && (
        <div className="p-5 md:p-7 space-y-6">
          {groups.map(({ app, entries }) => (
            <AppGroup key={app} app={app} entries={entries} />
          ))}
        </div>
      )}
    </article>
  )
}

function AppGroup({ app, entries }: { app: PatchApp; entries: PatchEntry[] }) {
  const meta = APP_META[app]
  // Kind-sortiert
  const sorted = [...entries].sort(
    (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)
  )
  // Pro Kind die Counts
  const kindCounts: Record<PatchKind, number> = { new: 0, changed: 0, fixed: 0, done: 0 }
  for (const e of entries) kindCounts[e.kind]++

  return (
    <section>
      <header className="flex flex-wrap items-center gap-2 mb-3 pb-2 border-b"
              style={{ borderColor: 'var(--border)' }}>
        <span className="text-xl">{meta.emoji}</span>
        <h3 className="text-sm font-bold">{meta.label}</h3>
        <span className="text-app-text-mute text-xs">·</span>
        <div className="flex items-center gap-1.5">
          {KIND_ORDER.map(k => kindCounts[k] > 0 && (
            <span key={k} className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{ background: KIND_META[k].bg, color: KIND_META[k].color }}>
              {kindCounts[k]} {KIND_META[k].label}
            </span>
          ))}
        </div>
      </header>
      <ul className="space-y-2">
        {sorted.map((e, i) => <EntryItem key={i} entry={e} />)}
      </ul>
    </section>
  )
}

function EntryItem({ entry }: { entry: PatchEntry }) {
  const km   = KIND_META[entry.kind]
  const [open, setOpen] = useState(false)
  const hasDetail = !!entry.detail

  return (
    <li
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: 'var(--border)', background: 'var(--bg-elev)' }}
    >
      <button
        onClick={() => hasDetail && setOpen(v => !v)}
        disabled={!hasDetail}
        className={`w-full text-left p-3 md:p-4 flex items-start gap-3 ${hasDetail ? 'hover:bg-app-elev-2/40 cursor-pointer' : 'cursor-default'}`}
      >
        <span
          className="shrink-0 w-1 self-stretch rounded-full"
          style={{ background: km.color }}
        />
        <span className="shrink-0 text-base mt-0.5" aria-hidden>{km.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{ background: km.bg, color: km.color }}>
              {km.label}
            </span>
            <span className="text-sm font-semibold leading-snug">{entry.title}</span>
          </div>
          {hasDetail && (
            <div className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
              open ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0 mt-0'
            }`}>
              <p className="text-sm text-app-text-dim leading-relaxed">{entry.detail}</p>
            </div>
          )}
        </div>
        {hasDetail && (
          <span className="shrink-0 text-app-text-mute text-sm transition-transform"
                style={{ transform: open ? 'rotate(90deg)' : 'rotate(0)' }}>›</span>
        )}
      </button>
    </li>
  )
}

function ChannelBadge({ channel }: { channel: Release['channel'] }) {
  const map: Record<Release['channel'], { bg: string; color: string }> = {
    'TestFlight': { bg: 'rgba(255,179,0,0.16)',  color: '#ffb300' },
    'App Store':  { bg: 'rgba(51,214,112,0.16)', color: '#33d670' },
    'Live':       { bg: 'rgba(58,98,255,0.16)',  color: '#3a62ff' },
  }
  const c = map[channel]
  return (
    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full"
          style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}55` }}>
      {channel.toUpperCase()}
    </span>
  )
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="text-center py-20">
      <div className="text-5xl mb-3">🪶</div>
      <h3 className="text-lg font-bold">Keine Patchnote-Einträge</h3>
      <p className="text-app-text-dim text-sm mt-1 mb-5">
        Mit den aktuellen Filtern findet sich nichts. Pass die Suche an oder leg sie zurück.
      </p>
      <button
        onClick={onReset}
        className="px-4 py-2 rounded-full border text-sm font-semibold hover:bg-app-elev-2"
        style={{ borderColor: 'var(--border)' }}
      >
        Filter zurücksetzen
      </button>
    </div>
  )
}

function BackgroundDecor() {
  return (
    <>
      <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full opacity-30 pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(138,68,238,0.5), transparent 70%)' }} />
      <div className="absolute top-1/3 -right-32 w-[28rem] h-[28rem] rounded-full opacity-20 pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(58,98,255,0.45), transparent 70%)' }} />
    </>
  )
}
